import { POLICIES_IN } from "@/lib/policies.in";
import { POLICIES_AE } from "@/lib/policies.ae";
import { POLICIES_NP } from "@/lib/policies.np";
import { SITE } from "@/lib/site";

/**
 * Legal documents for wateriia.com, which carries all three jurisdictions:
 *
 *   India   →  /policy/<slug>
 *   UAE     →  /ae/policy/<slug>
 *   Nepal   →  /np/policy/<slug>
 *
 * Every market's links therefore live on one domain, so a single set of URLs
 * can be pasted into Play Console and App Store Connect for all three listings.
 * wateriia.com.np keeps its own copy of the Nepali text for visitors of that
 * storefront — see the note at the top of `policies.np.js` about keeping the
 * two in sync.
 *
 * Every document is its own page with its own URL — the privacy policy, the
 * data retention policy, the data deletion page and the return/refund/
 * cancellation policy are deliberately NOT combined, because the stores link
 * to them individually. Keep the slugs stable: changing one breaks a live
 * store listing.
 */

export const LAST_UPDATED = "13 September 2026";

/** Default jurisdiction for wateriia.com when no region prefix is present. */
export const DEFAULT_REGION = "in";

export const REGIONS = {
  in: {
    key: "in",
    label: "India",
    /** URL prefix. India is unprefixed so existing links keep working. */
    base: "",
    currency: "INR",
    law: "Indian law",
    policies: POLICIES_IN,
    contact: {
      legal: "Wateriia India",
      email: SITE.email,
      phoneDisplay: SITE.phoneDisplay,
      phoneTel: SITE.phoneTel,
      address: SITE.address,
    },
  },
  ae: {
    key: "ae",
    label: "United Arab Emirates",
    base: "/ae",
    currency: "AED",
    law: "UAE federal law",
    policies: POLICIES_AE,
    // TODO(legal): swap for the UAE licensed entity once registered — see the
    // header of policies.ae.js.
    contact: {
      legal: "Wateriia India",
      email: SITE.email,
      phoneDisplay: SITE.phoneDisplay,
      phoneTel: SITE.phoneTel,
      address: SITE.address,
    },
  },
  np: {
    key: "np",
    label: "Nepal",
    base: "/np",
    currency: "NPR",
    law: "Nepali law",
    policies: POLICIES_NP,
    /** Nepali customers have their own storefront; policy text is identical. */
    siteUrl: "https://wateriia.com.np",
    contact: {
      legal: "Wateriia Enterprises",
      email: "info@wateriia.com.np",
      phoneDisplay: "+977 9744433838",
      phoneTel: "+9779744433838",
      address: "Ground Floor, Trade Tower Building, NH03, Kathmandu 06530, Nepal",
    },
  },
};

/** Order in which the jurisdiction switcher renders. */
export const REGION_ORDER = ["in", "ae", "np"];

/**
 * Shown in the footer and in the policy sidebar, in the order the store
 * listings reference them.
 */
export const POLICY_LINKS = [
  { slug: "privacy-policy", label: "Privacy Policy" },
  { slug: "terms-and-conditions", label: "Terms & Conditions" },
  { slug: "return-refund-policy", label: "Return, Refund & Cancellation Policy" },
  { slug: "shipping-policy", label: "Shipping & Delivery Policy" },
  { slug: "data-retention-policy", label: "Data Retention Policy" },
  { slug: "account-deletion", label: "Data & Account Deletion Policy" },
  { slug: "cookie-policy", label: "Cookie Policy" },
  { slug: "child-safety-policy", label: "Child Safety Policy" },
  { slug: "ecommerce-disclaimer", label: "E-commerce Disclaimer" },
  { slug: "support", label: "Support & Grievances" },
];

/** Older or shorter URLs that should keep resolving to a canonical slug. */
export const POLICY_ALIASES = {
  "refund-policy": "return-refund-policy",
  "returns-policy": "return-refund-policy",
  "cancellation-policy": "return-refund-policy",
  "return-refund-cancellation-policy": "return-refund-policy",
  "data-deletion": "account-deletion",
  "data-deletion-policy": "account-deletion",
  "delete-account": "account-deletion",
  "account-deletion-policy": "account-deletion",
  "data-retention": "data-retention-policy",
  "grievance-redressal": "support",
  terms: "terms-and-conditions",
  eula: "terms-and-conditions",
  privacy: "privacy-policy",
  cookies: "cookie-policy",
  "child-safety": "child-safety-policy",
};

/** A region key from the route, falling back to India. */
export function resolveRegion(regionKey) {
  const key = String(regionKey || "").trim().toLowerCase();
  return REGIONS[key] ? REGIONS[key] : REGIONS[DEFAULT_REGION];
}

/** Maps a URL slug (including legacy aliases) to a canonical slug, or null. */
export function resolvePolicySlug(slug) {
  if (!slug) return null;
  const key = String(slug).trim().toLowerCase();
  if (POLICY_LINKS.some((p) => p.slug === key)) return key;
  return POLICY_ALIASES[key] || null;
}

/** The document for a slug in a region, or null. */
export function getPolicy(regionKey, slug) {
  const region = resolveRegion(regionKey);
  const canonical = resolvePolicySlug(slug);
  if (!canonical) return null;
  return region.policies[canonical] || null;
}

/** Canonical href for a policy in a region, e.g. "/ae/policy/privacy-policy". */
export function policyHref(regionKey, slug) {
  const region = resolveRegion(regionKey);
  return `${region.base}/policy/${slug}`;
}
